import api from '../config/api';
import axios from 'axios';
import apiInstance from './api';

export interface TrainingCenter {
  id: string;
  name: string;
  description: string;
  logo?: string;
  banner?: string;
  rating: number;
  reviewCount: number;
  location: string;
  address?: string;
 
  country_id: number;
  country: {
    id: number;
    name: string;
  };
  wilaya_id: number;
  wilaya: {
    id: number;
    name: string;
  };
  city?: string;
  category_id?: string | null;
  category?: {
    id: string;
    name_french: string;
    name_english: string;
  } | null;
  specialties: string[];
  establishedYear: number;
  totalTrainings: number;
  activeTrainings: number;
  totalStudents: number;
  certifications: string[];
  contact: {
    phone: string;
    email: string;
    website?: string;
  };
  socialMedia?: {
    facebook?: string;
    linkedin?: string;
    twitter?: string;
  };
  isVerified: boolean;
  isPremium: boolean;
  createdAt: string;
}

export interface TrainingCenterReview {
  id: number;
  training_center_id: number;
  user_id: number;
  rating: number;
  comment: string;
  helpful_count: number;
  is_verified: boolean;
  created_at: string;
  updated_at: string;
  user: {
    id: number;
    username: string;
    firstName?: string;
    lastName?: string;
  };
}

export interface CreateTrainingCenterReviewData {
  rating: number;
  comment: string;
}

export interface TrainingCenterStats {
  totalCenters: number;
  totalTrainings: number;
  averageRating: number;
}

export interface TrainingCenterFilters {
  search?: string;
  country?: number; // Changed to number for country ID
  wilaya?: string;
  specialty?: string;
  verified?: string;
  premium?: string;
  minRating?: number;
  sortBy?: string;
  page?: number;
  limit?: number;
}

export const trainingCentersService = {
  // Get all training centers
  getAllTrainingCenters: async (filters: TrainingCenterFilters = {}): Promise<{
    centers: TrainingCenter[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> => {
    try {
      const params = new URLSearchParams();
      
      if (filters.search) params.append('search', filters.search);
      if (filters.country) params.append('country', filters.country.toString()); // Send country ID
      if (filters.wilaya) params.append('wilaya', filters.wilaya);
      if (filters.specialty) params.append('specialty', filters.specialty);
      if (filters.verified) params.append('verified', filters.verified);
      if (filters.premium) params.append('premium', filters.premium);
      if (filters.minRating) params.append('minRating', filters.minRating.toString());
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());

      const response = await axios.get(`${api.BASE_URL}/public/training-centers?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get training center by ID
  getTrainingCenter: async (id: string): Promise<TrainingCenter & {
    ratingStats: {
      averageRating: number;
      totalReviews: number;
      ratingDistribution: { 1: number; 2: number; 3: number; 4: number; 5: number };
    };
    viewCount: number;
  }> => {
    try {
      const response = await axios.get(`${api.BASE_URL}/public/training-centers/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get training center trainings
  getCenterTrainings: async (id: string, filters: any = {}): Promise<any[]> => {
    try {
      const params = new URLSearchParams();

      if (filters.search) params.append('search', filters.search);
      if (filters.format) params.append('format', filters.format);
      if (filters.sortBy) params.append('sortBy', filters.sortBy);

      const response = await axios.get(`${api.BASE_URL}/public/training-centers/${id}/trainings?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get training center stats
  getStats: async (): Promise<TrainingCenterStats> => {
    try {
      const response = await axios.get(`${api.BASE_URL}/public/training-centers/stats`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get specialties
  getSpecialties: async (): Promise<{ id: number; name: string }[]> => {
    try {
      const response = await axios.get(`${api.BASE_URL}/public/training-centers/specialties`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get categories
  getCategories: async (): Promise<{ id: number; name_french?: string; name_english?: string; name?: string }[]> => {
    try {
      const response = await axios.get(`${api.BASE_URL}/public/training-centers/categories`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get locations (wilayas)
  getLocations: async (): Promise<{ id: number; name: string }[]> => {
    try {
      const response = await axios.get(`${api.BASE_URL}/public/training-centers/locations`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Review methods
  getCenterReviews: async (centerId: string): Promise<TrainingCenterReview[]> => {
    try {
      const response = await axios.get(`${api.BASE_URL}/public/training-centers/${centerId}/reviews`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  createCenterReview: async (centerId: string, data: CreateTrainingCenterReviewData): Promise<TrainingCenterReview> => {
    try {
      const response = await apiInstance.post(`/public/training-centers/${centerId}/reviews`, data);
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        throw new Error(error.response.data.message || 'Error creating review');
      }
      throw error;
    }
  },

  markReviewHelpful: async (centerId: string, reviewId: number): Promise<void> => {
    try {
      await apiInstance.post(`/public/training-centers/${centerId}/reviews/${reviewId}/helpful`);
    } catch (error) {
      throw error;
    }
  },

  // Get similar centers
  getSimilarCenters: async (centerId: string, limit: number = 3): Promise<TrainingCenter[]> => {
    try {
      const response = await axios.get(`${api.BASE_URL}/public/training-centers/${centerId}/similar?limit=${limit}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Delete training center account permanently
  deleteAccount: async (): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await apiInstance.delete('/auth/training-center/account');
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};
